import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, call } from "./api";

export const qk = {
  config: ["config"] as const,
  me: ["me"] as const,
  dashboard: ["dashboard"] as const,
  plans: ["plans"] as const,
  plan: (id: string) => ["plan", id] as const,
  exercises: ["exercises"] as const,
  exercise: (id: string) => ["exercise", id] as const,
  exerciseHistory: (id: string) => ["exercise-history", id] as const,
  progress: ["progress"] as const,
  session: (id: string) => ["session", id] as const,
  training: (id: string) => ["training", id] as const,
  planShare: (id: string) => ["plan-share", id] as const,
  shared: (token: string) => ["shared", token] as const,
};

const noRetryOn4xx = (count: number, error: unknown) =>
  !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2;

/** Public server settings, available before sign-in. */
export const useConfig = () =>
  useQuery({ queryKey: qk.config, queryFn: () => call(api.config.$get()), staleTime: Infinity });
export const useMe = () => useQuery({ queryKey: qk.me, queryFn: () => call(api.me.$get()), retry: noRetryOn4xx });
export const useDashboard = () => useQuery({ queryKey: qk.dashboard, queryFn: () => call(api.dashboard.$get()) });
export const usePlans = () => useQuery({ queryKey: qk.plans, queryFn: () => call(api.plans.$get()) });
export const usePlan = (id: string) =>
  useQuery({ queryKey: qk.plan(id), queryFn: () => call(api.plans[":id"].$get({ param: { id } })), retry: noRetryOn4xx });
export const useExercises = () => useQuery({ queryKey: qk.exercises, queryFn: () => call(api.exercises.$get()) });
export const useExercise = (id: string) =>
  useQuery({
    queryKey: qk.exercise(id),
    queryFn: () => call(api.exercises[":id"].$get({ param: { id } })),
    retry: noRetryOn4xx,
  });
export const useExerciseHistory = (id: string) =>
  useQuery({
    queryKey: qk.exerciseHistory(id),
    queryFn: () => call(api.exercises[":id"].history.$get({ param: { id } })),
    retry: noRetryOn4xx,
  });
export const useProgress = () => useQuery({ queryKey: qk.progress, queryFn: () => call(api.progress.$get()) });
export const useSessionDetail = (id: string) =>
  useQuery({
    queryKey: qk.session(id),
    queryFn: () => call(api.sessions[":id"].$get({ param: { id } })),
    retry: noRetryOn4xx,
  });
export const useTraining = (id: string) =>
  useQuery({
    queryKey: qk.training(id),
    queryFn: () => call(api.sessions[":id"].training.$get({ param: { id } })),
    retry: noRetryOn4xx,
    // The guided workout keeps its own state; don't refetch underneath it.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

/** A mutation that refreshes all cached server data afterwards (like a page reload, but instant). */
export function useApiMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>, opts?: { invalidate?: boolean }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      if (opts?.invalidate !== false) return qc.invalidateQueries();
    },
  });
}
